import type { TargetingConfig } from '../../types/metaAdDraft';
import { cityCountryCode, defaultCityRadius, defaultPlaceRadius } from '../../lib/metaAdTargetingShape';
import { exclusionUnsupportedNote, isExcludableGeoType } from '../../lib/metaExclusionSupport';
import { countryName } from './countries';
import type { UnifiedGeoResult } from './geoSearch';

// Audience-builder — GEOGRAPHIC-HIERARCHY replacement on selection (matching Meta).
// Adding a broader area absorbs the narrower selections it contains; adding a narrower
// area already covered by a broader selection is blocked.
//
//   country  → supersedes EVERYTHING: hard-reset to just that country.
//   region   → removes existing selections INSIDE that state (path-matched), keeps
//              selections in OTHER states (so NSW-state + specific QLD areas is allowed,
//              but NOT QLD-state + a QLD suburb). Custom pins PERSIST regardless (they
//              carry no breadcrumb — documented limitation; not state-contained).
//   city/place/neighborhood/zip → additive; they don't absorb anything. Blocked if
//              already covered by a selected country or the containing state.
//
// CONTAINMENT is by the search-result PATH breadcrumb (path[0]=country, path[1]=state, …)
// — cheap + reliable, no geometry needed. Pure + deterministic.

export type GeoSelectionOutcome = 'added' | 'reset' | 'blocked';
export type GeoSelectionResult = { next: TargetingConfig; outcome: GeoSelectionOutcome; note?: string };

const CONTAINED_NOTE = "Already covered by a broader area you've selected.";
const DUPLICATE_NOTE = 'Already added.';

const norm = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase();

function pathHas(path: string[] | null | undefined, name: string): boolean {
  return Array.isArray(path) && path.some((p) => norm(p) === norm(name));
}

// The names of the currently-selected countries (codes → names), for path matching.
function selectedCountryNames(t: TargetingConfig): string[] {
  return t.countries.map((c) => norm(countryName(c))).filter(Boolean);
}

// Is this result inside one of the SELECTED countries? (its breadcrumb names that country)
function inSelectedCountry(path: string[] | null | undefined, t: TargetingConfig): boolean {
  const names = selectedCountryNames(t);
  return Array.isArray(path) && path.some((p) => names.includes(norm(p)));
}

// Is this result inside one of the SELECTED states/regions? (its breadcrumb names it)
function inSelectedRegion(path: string[] | null | undefined, t: TargetingConfig): boolean {
  return t.regions.some((r) => pathHas(path, r.name));
}

export function applyGeoSelection(
  t: TargetingConfig,
  result: UnifiedGeoResult,
  opts: { defaultCountryCode?: string | null } = {},
): GeoSelectionResult {
  const { geoType, id, name } = result;
  const path = result.path ?? null;
  // Store the parent country from the breadcrumb, else the account's search country (the
  // old per-type handlers' fallback) so a path-less result still records a country.
  const countryCode = cityCountryCode({ path }) ?? opts.defaultCountryCode ?? null;

  // COUNTRY supersedes everything → only the country remains.
  if (geoType === 'country') {
    return {
      next: {
        ...t,
        countries: [id],
        regions: [],
        cities: [],
        places: [],
        neighborhoods: [],
        zips: [],
        customLocations: [],
      },
      outcome: 'reset',
    };
  }

  // Don't add something already covered by a broader selection.
  if (inSelectedCountry(path, t)) return { next: t, outcome: 'blocked', note: CONTAINED_NOTE };
  if (geoType !== 'region' && inSelectedRegion(path, t)) return { next: t, outcome: 'blocked', note: CONTAINED_NOTE };

  // REGION absorbs same-state narrower selections; keeps other states + pins.
  if (geoType === 'region') {
    if (t.regions.some((r) => r.key === id)) return { next: t, outcome: 'blocked', note: DUPLICATE_NOTE };
    const notInThisState = (p: string[] | null | undefined) => !pathHas(p, name);
    return {
      next: {
        ...t,
        regions: [...t.regions.filter((r) => notInThisState(r.path)), { key: id, name, path, countryCode }],
        cities: t.cities.filter((c) => notInThisState(c.path)),
        places: t.places.filter((p) => notInThisState(p.path)),
        neighborhoods: t.neighborhoods.filter((n) => notInThisState(n.path)),
        zips: t.zips.filter((z) => notInThisState(z.path)),
      },
      outcome: 'added',
    };
  }

  // LOCAL CATCHMENT types — additive, dedupe by key, absorb nothing.
  //
  // city + neighborhood (incl. Meta's 'subcity', aliased to 'neighborhood' upstream by
  // resolveGeoType) DEFAULT to the radius-bearing cities channel (geo_locations.cities,
  // the city radius default of 17 km). Meta's source-type controls the POLYGON, not the
  // radius — the CHANNEL does; a suburb Meta types as `neighborhood` still gets a catchment
  // radius by default (matches Meta's own "Suburb + radius" behaviour, and restores the
  // pre-regression known-good from commit 17ecbf9, where suburbs published through cities).
  // The AM can switch any one to an EXACT-AREA keyed polygon per-location via
  // switchToExactArea (→ neighborhoods[], no radius) and back via switchToCatchment.
  // Dedupe across BOTH channels so a location already added (as catchment OR exact area)
  // isn't re-added.
  if (geoType === 'city' || geoType === 'neighborhood') {
    if (t.cities.some((c) => c.key === id) || t.neighborhoods.some((n) => n.key === id)) {
      return { next: t, outcome: 'blocked', note: DUPLICATE_NOTE };
    }
    return {
      next: { ...t, cities: [...t.cities, { key: id, name, radius: defaultCityRadius('kilometer'), distanceUnit: 'kilometer', path, countryCode }] },
      outcome: 'added',
    };
  }
  if (geoType === 'place') {
    if (t.places.some((p) => p.key === id)) return { next: t, outcome: 'blocked', note: DUPLICATE_NOTE };
    return {
      next: { ...t, places: [...t.places, { key: id, name, radius: defaultPlaceRadius('kilometer'), distanceUnit: 'kilometer', path, countryCode }] },
      outcome: 'added',
    };
  }
  // zip
  if (t.zips.some((z) => z.key === id)) return { next: t, outcome: 'blocked', note: DUPLICATE_NOTE };
  return { next: { ...t, zips: [...t.zips, { key: id, name, path, countryCode }] }, outcome: 'added' };
}

// Audience-builder — per-location CHANNEL switch (catchment radius <-> exact-area polygon),
// the polygon OPT-OUT for a selected location. A catchment location lives in cities[]
// (geo_locations.cities, WITH a radius); switching it to EXACT AREA moves it to
// neighborhoods[] (geo_locations.neighborhoods, the keyed polygon, NO radius) and back.
// Same Meta key, different Meta channel — the channel controls the radius, not the Meta
// source-type. Both return the { cities, neighborhoods } slice (a no-op slice when the key
// isn't in the source bucket / is already in the target). Pure. INCLUDE-bucket only —
// exclusion never routes through neighborhoods (Meta can't exclude them).
export function switchToExactArea(
  t: TargetingConfig,
  key: string,
): Pick<TargetingConfig, 'cities' | 'neighborhoods'> {
  const city = t.cities.find((c) => c.key === key);
  if (!city || t.neighborhoods.some((n) => n.key === key)) {
    return { cities: t.cities, neighborhoods: t.neighborhoods };
  }
  return {
    cities: t.cities.filter((c) => c.key !== key),
    neighborhoods: [
      ...t.neighborhoods,
      { key: city.key, name: city.name, path: city.path ?? null, countryCode: city.countryCode ?? null },
    ],
  };
}

export function switchToCatchment(
  t: TargetingConfig,
  key: string,
): Pick<TargetingConfig, 'cities' | 'neighborhoods'> {
  const n = t.neighborhoods.find((x) => x.key === key);
  if (!n || t.cities.some((c) => c.key === key)) {
    return { cities: t.cities, neighborhoods: t.neighborhoods };
  }
  return {
    neighborhoods: t.neighborhoods.filter((x) => x.key !== key),
    cities: [
      ...t.cities,
      {
        key: n.key,
        name: n.name,
        radius: defaultCityRadius('kilometer'),
        distanceUnit: 'kilometer',
        path: n.path ?? null,
        countryCode: n.countryCode ?? null,
      },
    ],
  };
}

// Audience-builder — manual location EXCLUSION add (the search's Exclude mode). Unlike
// the INCLUDE hierarchy above, exclusions are purely ADDITIVE into the `excluded`
// bucket: no broader-area absorption, no containment block (excluding a suburb INSIDE an
// included state is the whole point). Just dedupe by key within the excluded bucket.
//
// ONLY region / city / zip are excludable — Meta/Zernio's excludedLocations rejects
// countries, places, neighborhoods and custom pins, which would silently drop. We block
// them here (and the UI doesn't offer them) so an exclusion never vanishes unseen. Pure.
export function addGeoExclusion(
  t: TargetingConfig,
  result: UnifiedGeoResult,
  opts: { defaultCountryCode?: string | null } = {},
): GeoSelectionResult {
  const { geoType, id, name } = result;
  const path = result.path ?? null;
  const countryCode = cityCountryCode({ path }) ?? opts.defaultCountryCode ?? null;
  const e = t.excluded;

  if (!isExcludableGeoType(geoType)) {
    return { next: t, outcome: 'blocked', note: exclusionUnsupportedNote(geoType) };
  }

  if (geoType === 'region') {
    if (e.regions.some((r) => r.key === id)) return { next: t, outcome: 'blocked', note: DUPLICATE_NOTE };
    return { next: { ...t, excluded: { ...e, regions: [...e.regions, { key: id, name, path, countryCode }] } }, outcome: 'added' };
  }
  if (geoType === 'city') {
    if (e.cities.some((c) => c.key === id)) return { next: t, outcome: 'blocked', note: DUPLICATE_NOTE };
    return {
      next: { ...t, excluded: { ...e, cities: [...e.cities, { key: id, name, radius: defaultCityRadius('kilometer'), distanceUnit: 'kilometer', path, countryCode }] } },
      outcome: 'added',
    };
  }
  // zip
  if (e.zips.some((z) => z.key === id)) return { next: t, outcome: 'blocked', note: DUPLICATE_NOTE };
  return { next: { ...t, excluded: { ...e, zips: [...e.zips, { key: id, name, path, countryCode }] } }, outcome: 'added' };
}
